import React from 'react'
import Papa, { ParseError } from 'papaparse'
import { useTable, Column } from 'react-table';

type Rating = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

//"1046","","","Jar of Flies","1994","7","n","","",  ""
export interface AlbumModel {
  id: string // 186272
  firstName: string // Anouar
  lastName: string // Brahem
  firstNameLocalized: string
  lastNameLocalized: string
  title: string // Conte de l'incroyable amour
  releaseDate: number // 1992
  rating?: Rating | undefined;
  ownership: "n" | "o" | "w" // no | owned | wanted
  purchaseDate: string
  mediaType: "LP" | "CD"
  review: string
}

export const WINDOW_SIZE = 5;

export interface AlbumViewModel {
  id: string,
  artist: string,
  releaseDate: number,
  title: string,
  rating: string
}

type TableRow = AlbumViewModel

export function AlbumTable(props: {
  albums: AlbumModel[]
}) {
  const models: TableRow[] = (props.albums ?? []).map(album => {
    return ({
      id: album.id,
      artist: album.firstName !== '' 
        ? album.firstName + ' ' + album.lastName
        : album.lastName,
      title: album.title,
      rating: stars(album.rating),
      releaseDate: album.releaseDate
    });
  }).slice(0, WINDOW_SIZE);

  const data: TableRow[] = React.useMemo(
    () => models ?? [],
    [models]
  )

  const columns: Column<TableRow>[] = React.useMemo(
    () => [
      {
        Header: 'Rating',
        accessor: 'rating', // accessor is the "key" in the data
      },
      {
        Header: 'Artist',
        accessor: 'artist',
      },
      {
        Header: 'Title',
        accessor: 'title',
      },
      {
        Header: 'Release',
        accessor: 'releaseDate',
      },
    ],
    []
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data })

  return (
    <table {...getTableProps()}>
      <thead>
        {headerGroups.map(headerGroup => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map(column => (
              <th
                style={{ minWidth: '100px' }}
                {...column.getHeaderProps()}
              >
                {column.render('Header')}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map(row => {
          prepareRow(row)
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map(cell => {
                return (
                  <td
                    {...cell.getCellProps()}
                  >
                    {cell.render('Cell')}
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export function stars(rating: AlbumModel['rating']) {
  if (rating === undefined) {
    return ''
  }

  // Could I have written code to do this?
  // Yes of course, but I was very tired when I wrote this code and I frankly
  // could not be arsed.
  switch (rating) {
    case 1:
      return '✨'
    case 2:
      return '⭐'
    case 3:
      return '⭐✨'
    case 4:
      return '⭐⭐'
    case 5:
      return '⭐⭐✨'
    case 6:
      return '⭐⭐⭐'
    case 7:
      return '⭐⭐⭐✨'
    case 8:
      return '⭐⭐⭐⭐'
    case 9:
      return '⭐⭐⭐⭐✨'
    case 10:
      return '⭐⭐⭐⭐⭐'
    default:
      return ''
  }
}

function parseRating(rating: string): Rating | undefined {
  const number = parseInt(rating, 10)

  if (number === 0) {
    return undefined;
  }

  if (number < 0 || 10 < number) {
    console.warn("Parsed an unexpected rating:", rating)
    return undefined;
  }

  // @ts-ignore refinement
  return number;
}

interface Props {
  onParsed: (albums: AlbumModel[]) => void
}

interface State {
  errorMessage?: string
  parseErrors?: ParseError[]
  loading: boolean
}

export class FileInput extends React.Component<Props, State> {
  fileInput: React.RefObject<HTMLInputElement>

  constructor(props: Props) {
    super(props)
    this.fileInput = React.createRef()

    this.state = {
      loading: false
    }
  }

  parse = (input: string | File) => {
    Papa.parse(input, {
      complete: (results: Papa.ParseResult) => {

        if (results.errors.length !== 0) {
          const errorMessage = "File failed to parse"
          console.error(errorMessage, results.errors);
          this.setState({
            loading: false,
            errorMessage,
            parseErrors: results.errors
          });

          return;
        }

        const albums: AlbumModel[] = results.data.map(row => {
          const [
            id,
            firstName,
            lastName,
            firstNameLocalized,
            lastNameLocalized,
            title,
            releaseDate,
            rating,
            ownership,
            purchaseDate,
            mediaType,
            review
          ] = row;

          return {
            id,
            firstName,
            lastName,
            firstNameLocalized,
            lastNameLocalized,
            title,
            releaseDate: parseInt(releaseDate, 10),
            rating: parseRating(rating),
            ownership,
            purchaseDate,
            mediaType,
            review
          }
        })
        // This strips the CSV header.
        .filter(album => album.id !== 'RYM Album')


        console.info('CSV parsed successfully. Parsed result:', albums)
        this.props.onParsed(albums)
        this.setState({ loading: false })
      }
    })
  }

  loadSampleData = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    fetch('/mbo_albums_export.csv')
      .then((r) => r.text())
      .then(text => this.parse(text))
  }

  handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    console.info('The user clicked the submit button')

    this.setState({ loading: true })

    if ((this.fileInput.current?.files ?? []).length === 0) {
      const errorMessage = 'File did not succesfully upload'
      console.error(errorMessage)
      this.setState({
        loading: false,
        errorMessage
      })

      return
    }

    // @ts-ignore
    const file: File = this.fileInput.current.files[0]
    console.info('Got file', file)

    this.parse(file)
  }


  render() {
    const uploadButton = <button type="submit">
      Upload
    </button>

    const sampleDataButton = <button type="button" onClick={this.loadSampleData}>
      Load Sample Data
    </button>

    return (
      <>
        <form onSubmit={this.handleSubmit}>
          <input type="file" ref={this.fileInput} />
          <br />
          {uploadButton}  or  {sampleDataButton}
          {this.state.loading && "Loading"}
          {this.state.errorMessage}
          {this.state.parseErrors?.map(e => e.message)}
        </form>
      </>
    )
  }
}